import { logger } from '@/utils/logger/logger';
import { isChunkLoadError } from '@/utils/retry-lazy';
import { isTauri } from '@heidi/runtime/platform';

/**
 * Records that this tab has already spent its one automatic reload.
 *
 * `sessionStorage` is the only storage with the right lifetime: it survives the
 * reload we are about to perform (without which the marker is useless), it is
 * scoped to this tab so one tab's failure cannot suppress another's recovery,
 * and it is discarded when the tab closes so a new tab gets a fresh attempt.
 */
const RELOAD_ATTEMPTED_KEY = 'heidi.chunkReloadAttempted';

/**
 * Claims this tab's single automatic reload, returning `false` when it is
 * already spent or when the claim could not be recorded.
 *
 * Failing closed on an unusable `sessionStorage` (Safari private browsing,
 * blocked site data, quota exhaustion) is the safety property this whole module
 * rests on: with no marker that outlives the reload there is no way to tell the
 * first attempt from the hundredth, and a chunk that is genuinely gone from the
 * CDN would reload the tab forever. A dead surface the user can refresh by hand
 * is a bad experience; a tab that reloads itself in a loop is a worse one.
 */
const claimReloadAttempt = (): boolean => {
  try {
    const storage = window.sessionStorage;
    if (storage.getItem(RELOAD_ATTEMPTED_KEY) !== null) return false;
    storage.setItem(RELOAD_ATTEMPTED_KEY, String(Date.now()));
    // Read back rather than trust the write. A storage shim that silently
    // swallows writes would otherwise hand out an unlimited supply of "first"
    // attempts, which is the loop this guard exists to prevent.
    return storage.getItem(RELOAD_ATTEMPTED_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * Reloads the page at most once per tab to recover from a chunk-load failure.
 *
 * The failure this addresses is a long-lived tab holding HTML that references
 * chunk hashes from a deployment which has since rotated: every lazy boundary
 * that has not loaded yet 404s, the error reaches the app error boundary, and
 * the user is left on a generic error page. Re-fetching the document is the
 * entire fix, and it is cheap and safe for a static asset that failed to load.
 *
 * The marker is deliberately never cleared on a successful boot. Clearing it
 * would re-open the loop: a chunk that is missing rather than stale fails again
 * moments after the reload, and each failure would look like a first attempt.
 * One reload per tab is the cost of that guarantee.
 *
 * Returns whether a reload was started, so a caller can skip work it is about
 * to throw away. `beforeReload` runs only once the reload is certain, and before
 * it starts, so a report can record the outcome and still be sent.
 */
export const recoverFromChunkLoadError = (
  error: unknown,
  context: string,
  beforeReload?: () => void
): boolean => {
  if (!isChunkLoadError(error)) return false;
  if (typeof window === 'undefined') return false;

  // Desktop serves its chunks out of the signed app bundle, so the deployment
  // rotation this recovers from cannot reach it — a missing chunk there means a
  // broken build, which no reload mends. The reload would not be free either:
  // it tears down a webview that may still be attached to a native recording,
  // the harm `useWarnBeforeWebViewReloadWhileRecording` already exists to
  // prevent. Windows desktop serves the app over `http://tauri.localhost`, so
  // the protocol check below cannot stand in for this one.
  if (isTauri()) return false;

  // Only a document fetched from a server can come back with a fresh chunk
  // manifest. The `file://` occurrences in RUM are browser "Save page as"
  // copies of the app opened from disk, where the absolute `/_next/...` asset
  // paths resolve against the drive root and would 404 identically after any
  // number of reloads.
  const { protocol } = window.location;
  if (protocol !== 'http:' && protocol !== 'https:') return false;

  if (!claimReloadAttempt()) return false;

  let beforeReloadFailed = false;
  try {
    beforeReload?.();
  } catch {
    // Same as the log below: the attempt is claimed, so the reload must happen.
    beforeReloadFailed = true;
  }

  try {
    logger.warn('[chunk-recovery] reloading once to pick up a fresh chunk manifest', {
      context,
      beforeReloadFailed,
      error: error instanceof Error ? error.message : String(error),
    });
  } catch {
    // The attempt is already claimed; a logging failure must not strand the tab
    // on the error page it was about to leave.
  }
  window.location.reload();
  return true;
};
